import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isEvidenceCurrent, isPortfolioOptimizationEvidence } from "../contracts/portfolioOptimizationDecisionEvidence";
import { storePortfolioOptimizationEvidence } from "../services/portfolioOptimizationEvidenceSession";
import { acceptPortfolioOptimizationEvidence, fetchCurrentPortfolioOptimizationEvidence } from "../services/portfolioOptimizationEvidenceApi";
import { useAccess } from "../../../../access/AccessContext";
import { PERMISSIONS } from "../../../../access/capabilities";

const EvidenceContext = createContext({ evidence: null, acceptOptimization: () => false, getDecisionEvidence: () => ({ status: "UNAVAILABLE", evidence: null }) });

export function PortfolioOptimizationEvidenceProvider({ children }) {
  const access = useAccess();
  const canProduce = access.hasPermission(PERMISSIONS.optimizationEvidence.add);
  const [evidence, setEvidence] = useState(null);
  const [recovering, setRecovering] = useState(true);
  const [acceptanceError, setAcceptanceError] = useState(null);
  useEffect(() => {
    let mounted = true;
    fetchCurrentPortfolioOptimizationEvidence()
      .then((recovered) => {
        if (!mounted) return;
        if (recovered && isPortfolioOptimizationEvidence(recovered)) {
          setEvidence(recovered);
          storePortfolioOptimizationEvidence(recovered);
        }
      }).catch(() => null)
      .finally(() => { if (mounted) setRecovering(false); });
    return () => { mounted = false; };
  }, []);
  const acceptOptimization = useCallback(async (response) => {
    setAcceptanceError(null);
    if (!canProduce) return false;
    try {
      const accepted = await acceptPortfolioOptimizationEvidence(response);
      if (!isPortfolioOptimizationEvidence(accepted)) return false;
      storePortfolioOptimizationEvidence(accepted);
      setEvidence(accepted);
      return true;
    } catch (error) {
      const rejection = JSON.stringify(error?.response?.data || "").toLowerCase();
      const stale = /stale|portfolio settings changed|analytical settings changed/.test(rejection);
      setAcceptanceError(stale
        ? "The portfolio has changed since this optimization was calculated. Run the optimization again before accepting it."
        : "Unable to accept this optimization. Check your access and run the optimization again before retrying.");
      return false;
    }
  }, [canProduce]);
  const getDecisionEvidence = useCallback((currentContext) => evidence
    ? { status: isEvidenceCurrent(evidence, currentContext) ? "AVAILABLE" : "STALE", evidence: isEvidenceCurrent(evidence, currentContext) ? evidence : null }
    : { status: "UNAVAILABLE", evidence: null }, [evidence]);
  const value = useMemo(() => ({ evidence, recovering, canProduce, acceptanceError, acceptOptimization, getDecisionEvidence }), [evidence, recovering, canProduce, acceptanceError, acceptOptimization, getDecisionEvidence]);
  return <EvidenceContext.Provider value={value}>{children}</EvidenceContext.Provider>;
}

export const usePortfolioOptimizationEvidence = () => useContext(EvidenceContext);
